<script setup>
import { computed, markRaw, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useStore, useMapGetter } from 'dashboard/composables/store';
import { STATUS } from 'dashboard/store/constants';
import { formatTime } from '@chatwoot/utils';
import CheckCircle from '@primeicons/vue/check-circle';
import Clock from '@primeicons/vue/clock';
import Comments from '@primeicons/vue/comments';
import Inbox from '@primeicons/vue/inbox';
import Reply from '@primeicons/vue/reply';
import Send from '@primeicons/vue/send';
import format from 'date-fns/format';
import fromUnixTime from 'date-fns/fromUnixTime';
import BarChart from 'shared/components/charts/BarChart.vue';
import Spinner from 'dashboard/components-next/spinner/Spinner.vue';
import ReportHeader from './components/ReportHeader.vue';
import ReportFilterSelector from './components/FilterSelector.vue';

const METRICS = {
  conversations: 'conversations_count',
  resolutions: 'resolutions_count',
};

const store = useStore();
const { t } = useI18n();

const accountSummary = useMapGetter('getAccountSummary');
const summaryStatus = useMapGetter('getAccountSummaryFetchingStatus');
const accountReport = useMapGetter('getAccountReports');

const groupBy = ref('day');

const formatCount = value => Number(value || 0).toLocaleString();
const formatDuration = value => formatTime(value) || '0s';

const summaryCards = computed(() => [
  {
    key: 'conversations_count',
    label: t('REPORT_DASHBOARD.METRICS.CONVERSATIONS'),
    value: formatCount(accountSummary.value.conversations_count),
    helper: t('REPORT_DASHBOARD.METRICS.CONVERSATIONS_HELPER'),
    icon: markRaw(Comments),
  },
  {
    key: 'resolutions_count',
    label: t('REPORT_DASHBOARD.METRICS.RESOLUTIONS'),
    value: formatCount(accountSummary.value.resolutions_count),
    helper: t('REPORT_DASHBOARD.METRICS.RESOLUTIONS_HELPER'),
    icon: markRaw(CheckCircle),
  },
  {
    key: 'avg_first_response_time',
    label: t('REPORT_DASHBOARD.METRICS.FIRST_RESPONSE_TIME'),
    value: formatDuration(accountSummary.value.avg_first_response_time),
    helper: t('REPORT_DASHBOARD.METRICS.AVERAGE_IN_PERIOD'),
    icon: markRaw(Reply),
  },
  {
    key: 'avg_resolution_time',
    label: t('REPORT_DASHBOARD.METRICS.RESOLUTION_TIME'),
    value: formatDuration(accountSummary.value.avg_resolution_time),
    helper: t('REPORT_DASHBOARD.METRICS.AVERAGE_IN_PERIOD'),
    icon: markRaw(Clock),
  },
  {
    key: 'incoming_messages_count',
    label: t('REPORT_DASHBOARD.METRICS.INCOMING_MESSAGES'),
    value: formatCount(accountSummary.value.incoming_messages_count),
    helper: t('REPORT_DASHBOARD.METRICS.IN_PERIOD'),
    icon: markRaw(Inbox),
  },
  {
    key: 'outgoing_messages_count',
    label: t('REPORT_DASHBOARD.METRICS.OUTGOING_MESSAGES'),
    value: formatCount(accountSummary.value.outgoing_messages_count),
    helper: t('REPORT_DASHBOARD.METRICS.IN_PERIOD'),
    icon: markRaw(Send),
  },
]);

const formatLabel = timestamp => {
  const date = fromUnixTime(timestamp);

  if (groupBy.value === 'month') return format(date, 'MMM yyyy');
  if (groupBy.value === 'year') return format(date, 'yyyy');
  if (groupBy.value === 'week') return format(date, 'dd MMM');
  return format(date, 'dd MMM');
};

const buildChart = (metric, label, color) => {
  const data = accountReport.value.data[metric] || [];
  return {
    labels: data.map(item => formatLabel(item.timestamp)),
    datasets: [
      {
        label,
        data: data.map(item => item.value),
        backgroundColor: color,
        borderRadius: 4,
        maxBarThickness: 36,
      },
    ],
  };
};

const conversationChart = computed(() =>
  buildChart(
    METRICS.conversations,
    t('REPORT_DASHBOARD.CHARTS.CONVERSATIONS'),
    '#18d24b'
  )
);

const resolutionChart = computed(() =>
  buildChart(
    METRICS.resolutions,
    t('REPORT_DASHBOARD.CHARTS.RESOLUTIONS'),
    '#90939a'
  )
);

const chartOptions = {
  plugins: {
    legend: { display: false },
  },
};

const fetchReports = filter => {
  const payload = {
    from: filter.from,
    to: filter.to,
    groupBy: filter.groupBy?.period || 'day',
    businessHours: filter.businessHours,
  };

  groupBy.value = payload.groupBy;
  store.dispatch('fetchAccountSummary', payload);
  Object.values(METRICS).forEach(metric => {
    store.dispatch('fetchAccountReport', { ...payload, metric });
  });
};
</script>

<template>
  <ReportHeader
    :header-title="t('REPORT_DASHBOARD.HEADER')"
    :header-description="t('REPORT_DASHBOARD.DESCRIPTION')"
  />

  <div class="flex flex-col gap-5 pb-6">
    <ReportFilterSelector
      show-group-by-filter
      :show-business-hours-switch="false"
      @filter-change="fetchReports"
    />

    <section
      class="grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-3"
      :aria-label="t('REPORT_DASHBOARD.SUMMARY')"
    >
      <article
        v-for="metric in summaryCards"
        :key="metric.key"
        class="flex min-h-32 items-start justify-between gap-4 rounded-lg border border-n-weak bg-n-solid-2 p-5"
      >
        <div class="min-w-0">
          <p class="text-sm font-medium text-n-slate-11">
            {{ metric.label }}
          </p>
          <Spinner v-if="summaryStatus === STATUS.FETCHING" class="mt-3" />
          <p
            v-else-if="summaryStatus === STATUS.FAILED"
            class="mt-3 text-sm text-n-ruby-10"
          >
            {{ t('REPORT.SUMMARY_FETCHING_FAILED') }}
          </p>
          <p v-else class="mt-2 text-2xl font-semibold text-n-slate-12">
            {{ metric.value }}
          </p>
          <p class="mt-1 text-xs text-n-slate-10">
            {{ metric.helper }}
          </p>
        </div>
        <span
          class="flex size-9 flex-shrink-0 items-center justify-center rounded-md bg-n-teal-3 text-n-teal-11"
        >
          <component :is="metric.icon" :size="18" />
        </span>
      </article>
    </section>

    <section class="grid grid-cols-1 gap-4 xl:grid-cols-2">
      <article class="rounded-lg border border-n-weak bg-n-solid-2 p-5">
        <div class="mb-5">
          <h3 class="text-base font-medium text-n-slate-12">
            {{ t('REPORT_DASHBOARD.CHARTS.CONVERSATIONS') }}
          </h3>
          <p class="mt-1 text-sm text-n-slate-10">
            {{ t('REPORT_DASHBOARD.CHARTS.CONVERSATIONS_HELPER') }}
          </p>
        </div>
        <div class="h-72">
          <woot-loading-state
            v-if="accountReport.isFetching[METRICS.conversations]"
            class="text-xs"
            :message="t('REPORT.LOADING_CHART')"
          />
          <BarChart
            v-else-if="conversationChart.labels.length"
            :collection="conversationChart"
            :chart-options="chartOptions"
          />
          <div
            v-else
            class="flex h-full items-center justify-center text-sm text-n-slate-10"
          >
            {{ t('REPORT_DASHBOARD.NO_DATA') }}
          </div>
        </div>
      </article>

      <article class="rounded-lg border border-n-weak bg-n-solid-2 p-5">
        <div class="mb-5">
          <h3 class="text-base font-medium text-n-slate-12">
            {{ t('REPORT_DASHBOARD.CHARTS.RESOLUTIONS') }}
          </h3>
          <p class="mt-1 text-sm text-n-slate-10">
            {{ t('REPORT_DASHBOARD.CHARTS.RESOLUTIONS_HELPER') }}
          </p>
        </div>
        <div class="h-72">
          <woot-loading-state
            v-if="accountReport.isFetching[METRICS.resolutions]"
            class="text-xs"
            :message="t('REPORT.LOADING_CHART')"
          />
          <BarChart
            v-else-if="resolutionChart.labels.length"
            :collection="resolutionChart"
            :chart-options="chartOptions"
          />
          <div
            v-else
            class="flex h-full items-center justify-center text-sm text-n-slate-10"
          >
            {{ t('REPORT_DASHBOARD.NO_DATA') }}
          </div>
        </div>
      </article>
    </section>
  </div>
</template>
